import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
import type {
  ApproveOnboardingRequest,
  CreateCounterpartyRequest,
  RejectOnboardingRequest
} from "../../../api/Api";
import { OnboardingStatusFilter } from "../../../components/onboardingRequests/types";
import { counterpartyKeys } from "../../tradingCounterparties/dataHooks/counterpartyQueries";

export const onboardingRequestKeys = {
  all: ["onboardingRequests"] as const,
  lists: () => [...onboardingRequestKeys.all, "list"] as const,
  list: (status: OnboardingStatusFilter = "Pending") =>
    [...onboardingRequestKeys.lists(), status] as const,
  mine: () => [...onboardingRequestKeys.all, "mine"] as const
};

export const useOnboardingRequests = (
  statusFilter: OnboardingStatusFilter = "Pending",
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: onboardingRequestKeys.list(statusFilter),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyOnboardingRequests.list({
        status: statusFilter
      });
      return response.data;
    },
    refetchInterval: options?.refetchInterval
  });
};

export const useMyOnboardingRequests = () => {
  return useQuery({
    queryKey: onboardingRequestKeys.mine(),
    queryFn: async () => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyOnboardingRequests.mine();
      return response.data;
    }
  });
};

type MutationCallbacks = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useSubmitOnboardingRequest = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateCounterpartyRequest) => {
      const apiClient = await getAuthenticatedApiClient();
      const response =
        await apiClient.counterpartyOnboardingRequests.submit(body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingRequestKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};

export const useApproveOnboardingRequest = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      id: string;
      body: ApproveOnboardingRequest;
    }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyOnboardingRequests.approve(
        vars.id,
        vars.body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingRequestKeys.all });
      queryClient.invalidateQueries({ queryKey: counterpartyKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};

export const useRejectOnboardingRequest = (options?: MutationCallbacks) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: { id: string; body: RejectOnboardingRequest }) => {
      const apiClient = await getAuthenticatedApiClient();
      const response = await apiClient.counterpartyOnboardingRequests.reject(
        vars.id,
        vars.body
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingRequestKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => options?.onError?.(error as Error)
  });
};
